<template lang="pug">
  #member-center-settings-individual
    .level.is-mobile
      .level-left
        .level-item
          WtTitle(h1) Edit details for {{ member.username }}
        .level-item
          BTag(
            v-if="member.programAdmin"
            type="is-action"
          ) ADMIN
      .level-right
        .level-item
          WtButton(@click.native="backToAllMembers()")
            | BACK TO ALL MEMBERS

    //- FORM
    WtTile(ancestor)
      WtTile(parent)
        WtTile(child :is-loading="deleteInProgress")
          MemberCenterSettingsIndividualForm(
            :key="memberKey"
            :member="member"
            :member-copy="memberCopy"
            @admin-updated="refreshMemberData"
          )

    WtTile(ancestor v-if="member.deviceApi && $auth.user().isWtAdmin")
      WtTile(parent)
        WtTile(child v-if="member.id")
          MemberDeviceManagement(
            :member="member"
            :member-id="member.id"
            @device-disconnected="refreshMemberData"
          )

    WtTile(ancestor v-if="$auth.user().isWtAdmin && member.deviceApi !== 'Manual entry'")
      WtTile(parent)
        WtTile(child v-if="member.id")
          AdminSuperDeviceDiagnostics(:member="member")

    WtTile(ancestor)
      WtTile(parent)
        WtTile(child v-if="member.id")
          MemberSettingsEditStepEntriesForm(:member="member")
    WtTile(ancestor v-if="$auth.user().isWtAdmin")
      WtTile(parent)
        WtTile(child v-if="member.id")
          MemberSettingsEditMetricEntriesForm(:member="member")
    WtTile(ancestor v-if="$auth.user().isWtAdmin").has-background-danger
      p ONLY SHOWS FOR BE WELL SUPER ADMINS
      WtTile(parent)
        WtTile(child v-if="member.id")
          MemberMilestones(:memberId="member.id")
    WtTile(ancestor v-if="$auth.user().isWtAdmin").has-background-danger
      p ONLY SHOWS FOR BE WELL SUPER ADMINS
      WtTile(parent)
        WtTile(child v-if="member.id")
          WtTitle Points
          AdminSuperAssignPoints(:member-id="member.id" :member="member" @points-added="refreshPointsTable")
          MemberPointsTable(ref="pointsTable" :memberId="member.id")
    WtTile(ancestor v-if="$auth.user().isWtAdmin").has-background-danger
      p ONLY SHOWS FOR BE WELL SUPER ADMINS
      WtTile(parent)
        WtTile(child v-if="member.id")
          AdminSuperMemberBadges(:memberId="member.id" :member="member")
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import MemberCenterSettingsIndividualForm from "@@/MemberCenterAdmin/MemberCenterSettingsIndividualForm";
import MemberSettingsEditMetricEntriesForm from "@@/MemberCenterAdmin/MemberSettingsEditMetricEntriesForm";
import MemberSettingsEditStepEntriesForm from "@@/MemberCenterAdmin/MemberSettingsEditStepEntriesForm";
import MemberDeviceManagement from "@@/MemberCenterAdmin/MemberDeviceManagement";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTile from "@@/WtTile/WtTile";
import WtButton from "@@/WtButton/WtButton";
import AdminSuperDeviceDiagnostics from "@@/SuperAdminDevices/DeviceDiagnostics.vue";

const MemberMilestones = () =>
  import(
    /* webpackChunkName: "MemberMilestones" */ "@@/MemberCenterAdminSuper/AdminSuperMemberMilestones"
  ).catch(() => {
    window.location.reload();
  });
const AdminSuperAssignPoints = () =>
  import(
    /* webpackChunkName: "AdminSuperAssignPoints" */ "@@/MemberCenterAdminSuper/AdminSuperAssignPoints"
  ).catch(() => {
    window.location.reload();
  });
const MemberPointsTable = () =>
  import(
    /* webpackChunkName: "MemberPointsTable" */ "@@/Members/Points/MemberPointsTable"
  ).catch(() => {
    window.location.reload();
  });
const AdminSuperMemberBadges = () =>
  import(
    /* webpackChunkName: "AdminSuperMemberBadges" */ "@@/MemberCenterAdminSuper/AdminSuperMemberBadges"
  ).catch(() => {
    window.location.reload();
  });

export default {
  name: "MemberCenterSettingsIndividual",

  components: {
    AdminSuperDeviceDiagnostics,
    WtTitle,
    WtButton,
    WtTile,
    MemberCenterSettingsIndividualForm,
    MemberSettingsEditMetricEntriesForm,
    MemberSettingsEditStepEntriesForm,
    MemberDeviceManagement,
    MemberMilestones,
    AdminSuperAssignPoints,
    MemberPointsTable,
    AdminSuperMemberBadges
  },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  data() {
    return {
      member: {},
      memberCopy: {},
      memberKey: 0
    };
  },

  computed: {
    ...mapGetters("members", ["deleteInProgress"])
  },

  async mounted() {
    this.trackFeatureView("admin", "member_profile_edit");

    const memberData = await this.fetchMember({
      memberId: this.$route.params.id,
      setMember: false
    });

    // set registeredDate to a datepicker readable format
    memberData.registeredDate = this.unixDateTime(
      memberData.registeredDate
    ).dateObject;

    // Assign with Vue.set to ensure full reactivity on all properties
    this.$set(this, "member", memberData);

    // clone a copy for resetting values
    this.memberCopy = this._.cloneDeep(this.member);
  },

  methods: {
    ...mapActions("members", ["fetchMember", "setLoadedStatus"]),

    backToAllMembers() {
      const backTarget =
        this.$route.query.membersListOrigin === "super"
          ? { name: "AdminSuperMemberCenter" }
          : { name: "MemberCenter" };

      this.$router.push(backTarget);

      this.setLoadedStatus({
        key: "memberLoaded",
        value: false
      });
    },

    async refreshMemberData() {
      const memberData = await this.fetchMember({
        memberId: this.$route.params.id,
        setMember: false
      });
      this.$set(this, "member", memberData);
      this.memberKey++;
    },

    refreshPointsTable() {
      this.$refs.pointsTable.refreshTable();
    }
  }
};
</script>

<style lang="sass">
.date-picker-columns
  margin-top: 5px

.set-date-range-button
  margin-top: 31px
</style>
